import React from 'react'
import { ListGroup, ListGroupItem, Button } from 'reactstrap'
import { filter, findIndex } from 'lodash'
import { useTranslation } from 'react-i18next'
import QtyInput from 'ui/forms/qty_input'
import resCn from 'lib/responsive_classes'

const TicketTypesList = ({ data, type, maxQtyReservation, onTicketQtyChanged, resSize }) => {
  const { t, i18n } = useTranslation()

  const TicketItem = (props) => {
    const {
      id,
      alternative_id,
      name,
      description,
      total_price,
      total_price_label,
      discount,
      discount_label,
      additional_fee,
      additional_fee_label,
      base_price,
      base_price_label,
      qty,
      is_available,
      available,
      show_price,
      thumbnail,
      seat_capacity,
      luggage_capacity,
      large_luggage_capacity,
      max_tickets,
      min_tickets
    } = props

    const Thumbnail = () => {
      if (!thumbnail) return null
      return (
        <div className="text-center me-0 me-md-4">
          <img src={thumbnail} style={{ width: '130px' }} alt={name} />
        </div>
      )
    }

    const Title = () => (
      <div
        className={resCn({
          wide: 'text-md-start ps-md-3',
          slim: 'flex-column text-center align-self-center',
          all: 'd-flex'
        }, resSize)}
      >
        <div>
          <strong>{name}</strong>
        </div>
        <div>
          <small>{description}</small>
        </div>
        {props.type === 'vehicle' && (
          <div>
            <span className="me-3">
              <i className="fa fa-user" /> {i18n.t('ticket_types_list.vehicle_seats', {seat_capacity: seat_capacity})}
            </span>
            <span className="me-1">
              <i className="fa fa-shopping-bag me-2" />
              {large_luggage_capacity > 0 ? i18n.t('ticket_types_list.vehicle_luggage_capacity_with_large_suitcases', {luggage_capacity: luggage_capacity, large_luggage_capacity: large_luggage_capacity}) : i18n.t('ticket_types_list.vehicle_luggage_capacity_without_large_suitcases', {luggage_capacity: luggage_capacity})}
            </span>
          </div>
        )}
      </div>
    )

    const Availability = () => {
      if (!is_available || available <= 0) return null
      return (
        <small
          className={resCn({
            wide: 'me-md-5',
            slim: 'me-0',
            all: 'd-flex align-self-center'
          }, resSize)}
        >
          {i18n.t('ticket_types_list.available_qty', { qty: available })}
        </small>
      )
    }

    const Price = () => {
      if (!show_price) return null

      return (
        <div
          className={resCn({
            wide: 'flex-md-row me-md-5 mb-md-0',
            slim: 'flex-column-reverse me-0 mb-1',
            all: 'd-flex align-self-center justify-content-center price'
          }, resSize)}
          style={{ fontSize: '18px' }}

        >
          {additional_fee > 0 && (
            <small
              className={resCn({
                wide: 'me-md-5',
                slim: 'me-0',
                all: 'd-flex align-self-center text-info'
              }, resSize)}
            >
              {i18n.t('ticket_types_list.booking_fee', { amount: additional_fee_label })}
            </small>
          )}
          <div className="text-center">
            {parseFloat(discount) > 0 ? (
              <>
                <span className="me-2 text-danger">{total_price_label}</span>
                <span style={{ textDecoration: 'line-through' }}>
                  <small>{base_price_label}</small>
                </span>
              </>
            ) : (
              total_price_label
            )}
          </div>
        </div>
      )
    }

    const Qty = () => {
      if (!is_available) {
        return (
          <span
            className="bg-light text-center"
            style={{
              padding: '10px 48px',
              background: 'grey',
              borderRadius: '10px'
            }}
          >
            {i18n.t('ticket_types_list.sold_out')}
          </span>
        )
      }

      if (max_tickets === 1 || maxQtyReservation === 1) {
        if (qty === 1) {
          return (
            <Button
              color="primary"
              className="text-white"
              style={{ width: '160px' }}
              onClick={() => {
                const newtickets = [...data]
                const indexChanged = findIndex(newtickets, { id })
                newtickets[indexChanged].qty = 0
                onTicketQtyChanged(newtickets)
              }}
            >
              <i className="far fa-check-square me-2"></i>
              {i18n.t('ticket_types_list.added')}
            </Button>
          )
        } else {
          return (
            <Button
              color="light"
              style={{ width: '160px' }}
              onClick={() => {
                const newtickets = [...data]
                const indexChanged = findIndex(newtickets, { id })
                newtickets[indexChanged].qty = 1
                onTicketQtyChanged(newtickets)
              }}
            >
              <i className="far fa-square me-2"></i>
              {i18n.t('ticket_types_list.add')}
            </Button>
          )
        }
      }

      const ticket = data.find((t) => t.id === id)
      const totalTickets = data.reduce((acc, ticket) => acc + ticket.qty, 0)

      return (
        <div className="d-flex align-self-center">
          <QtyInput
            id={id}
            alternativeId={alternative_id}
            value={qty}
            onIncrement={() => {
              const newtickets = [...data]
              const indexChanged = findIndex(newtickets, { id })
              newtickets[indexChanged].qty += 1
              onTicketQtyChanged(newtickets)
            }}
            onDecrement={() => {
              const newtickets = [...data]
              const indexChanged = findIndex(newtickets, { id })
              const newQty =
                newtickets[indexChanged].qty <= 0
                  ? 0
                  : newtickets[indexChanged].qty - 1
              newtickets[indexChanged].qty = newQty
              onTicketQtyChanged(newtickets)
            }}
            isMaxQty={
              (ticket.available ? ticket.qty >= ticket.available : false) ||
              (maxQtyReservation ? totalTickets >= maxQtyReservation : false)
            }
          />
        </div>
      )
    }

    return (
      <ListGroupItem
        key={id}
        className={resCn({
          wide: 'flex-md-row',
          slim: 'flex-column',
          all: 'rounded-0 d-flex justify-content-between'
        }, resSize)}
      >
        <div
          className={resCn({
            wide: 'flex-md-row justify-content-md-start pe-md-3',
            slim: 'flex-column justify-content-center pe-0',
            all: 'd-flex'
          }, resSize)}
        >
          <Thumbnail />
          <Title />
        </div>
        <div
          className={resCn({
            wide: 'flex-md-row',
            slim: 'flex-column',
            all: 'd-flex justify-content-center'
          }, resSize)}
        >
          <Availability />
          <Price />
          <Qty />
        </div>
      </ListGroupItem>
    )
  }

  return (
    <ListGroup>
      {filter(data, { visible: true }).map((ticket) => (
        <TicketItem {...ticket} key={ticket.id} />
      ))}
    </ListGroup>
  )
}

export default TicketTypesList
